/** Numeric date/time helpers. Never parse with `new Date(string)`. */

export type TemporalType = "date" | "datetime-local" | "time" | "month" | "week";

export type DateParts = { year: number; month: number; day: number };
export type TimeParts = { hour: number; minute: number };
export type DateTimeParts = DateParts & TimeParts;
export type MonthParts = { year: number; month: number };

export type CalendarCell = DateParts & { inMonth: boolean };

export const WEEKDAYS_SHORT = ["L", "M", "M", "J", "V", "S", "D"] as const;
export const WEEKDAYS_LONG = [
  "domingo",
  "lunes",
  "martes",
  "miércoles",
  "jueves",
  "viernes",
  "sábado",
] as const;
export const MONTHS_LONG = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
] as const;
export const MONTHS_SHORT = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
] as const;

export function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function isValidYmd(year: number, month: number, day: number): boolean {
  if (!Number.isInteger(year) || year < 1) return false;
  if (!Number.isInteger(month) || month < 1 || month > 12) return false;
  if (!Number.isInteger(day) || day < 1 || day > daysInMonth(year, month)) return false;
  return true;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function parseDateValue(value: string): DateParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!isValidYmd(year, month, day)) return null;
  return { year, month, day };
}

export function parseTimeValue(value: string): TimeParts | null {
  const match = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

export function parseDateTimeValue(value: string): DateTimeParts | null {
  const [datePart, timePart] = value.trim().split("T");
  if (!datePart || timePart == null) return null;
  const date = parseDateValue(datePart);
  const time = parseTimeValue(timePart || "00:00");
  if (!date || !time) return null;
  return { ...date, ...time };
}

export function parseMonthValue(value: string): MonthParts | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || year < 1) return null;
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  return { year, month };
}

export function formatDateValue(parts: DateParts): string {
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

export function formatTimeValue(parts: TimeParts): string {
  return `${pad2(parts.hour)}:${pad2(parts.minute)}`;
}

export function formatDateTimeValue(parts: DateTimeParts): string {
  return `${formatDateValue(parts)}T${formatTimeValue(parts)}`;
}

export function formatMonthValue(parts: MonthParts): string {
  return `${parts.year}-${pad2(parts.month)}`;
}

export function toDateValue(d: Date): string {
  return formatDateValue({ year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() });
}

export function toTimeValue(d: Date): string {
  return formatTimeValue({ hour: d.getHours(), minute: d.getMinutes() });
}

export function toDateTimeValue(d: Date): string {
  return formatDateTimeValue({
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  });
}

export function toMonthValue(d: Date): string {
  return formatMonthValue({ year: d.getFullYear(), month: d.getMonth() + 1 });
}

function localIsoFromParts(year: number, month: number, day: number, hour: number, minute: number, second = 0, ms = 0): string {
  return new Date(year, month - 1, day, hour, minute, second, ms).toISOString();
}

export function dateTimeValueToIso(value: string): string {
  const parts = parseDateTimeValue(value);
  if (!parts) {
    throw new Error(`Invalid datetime-local value: ${value}`);
  }
  return localIsoFromParts(parts.year, parts.month, parts.day, parts.hour, parts.minute);
}

export function dateValueStartIso(value: string): string {
  const parts = parseDateValue(value);
  if (!parts) {
    throw new Error(`Invalid date value: ${value}`);
  }
  return localIsoFromParts(parts.year, parts.month, parts.day, 0, 0, 0, 0);
}

export function dateValueEndIso(value: string): string {
  const parts = parseDateValue(value);
  if (!parts) {
    throw new Error(`Invalid date value: ${value}`);
  }
  return localIsoFromParts(parts.year, parts.month, parts.day, 23, 59, 59, 999);
}

export function formatClock12(hour: number, minute: number): string {
  const period = hour < 12 ? "a. m." : "p. m.";
  const display = hour % 12 || 12;
  return `${display}:${pad2(minute)} ${period}`;
}

export function to12Hour(hour: number): { hour12: number; isPm: boolean } {
  return { hour12: hour % 12 || 12, isPm: hour >= 12 };
}

export function from12Hour(hour12: number, minute: number, isPm: boolean): TimeParts {
  const hour = (hour12 % 12) + (isPm ? 12 : 0);
  return { hour, minute };
}

export function formatTemporalDisplay(type: string | undefined, value: string): string {
  if (!value) return "";
  if (type === "time") {
    const time = parseTimeValue(value);
    if (!time) return value;
    return formatClock12(time.hour, time.minute);
  }
  if (type === "date") {
    const date = parseDateValue(value);
    if (!date) return value;
    return `${pad2(date.day)}/${pad2(date.month)}/${date.year}`;
  }
  if (type === "datetime-local") {
    const parts = parseDateTimeValue(value);
    if (!parts) return value;
    return `${pad2(parts.day)}/${pad2(parts.month)}/${parts.year}, ${formatClock12(parts.hour, parts.minute)}`;
  }
  if (type === "month") {
    const month = parseMonthValue(value);
    if (!month) return value;
    return `${MONTHS_LONG[month.month - 1]} de ${month.year}`;
  }
  return value;
}

export function dayAriaLabel(parts: DateParts): string {
  const date = new Date(parts.year, parts.month - 1, parts.day);
  const weekday = WEEKDAYS_LONG[date.getDay()];
  const month = MONTHS_LONG[parts.month - 1].toLowerCase();
  return `${weekday}, ${parts.day} de ${month} de ${parts.year}`;
}

export function monthGrid(year: number, month: number): CalendarCell[] {
  const first = new Date(year, month - 1, 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = new Date(year, month - 1, 1 - mondayOffset);
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    cells.push({
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      inMonth: d.getFullYear() === year && d.getMonth() + 1 === month,
    });
  }
  return cells;
}

export function addMonths(parts: MonthParts, delta: number): MonthParts {
  const date = new Date(parts.year, parts.month - 1 + delta, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export function addDays(parts: DateParts, delta: number): DateParts {
  const date = new Date(parts.year, parts.month - 1, parts.day + delta);
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

export function startOfWeek(parts: DateParts): DateParts {
  const date = new Date(parts.year, parts.month - 1, parts.day);
  const mondayOffset = (date.getDay() + 6) % 7;
  return addDays(parts, -mondayOffset);
}

export function endOfWeek(parts: DateParts): DateParts {
  return addDays(startOfWeek(parts), 6);
}

export function dateOrd(parts: DateParts): number {
  return parts.year * 10000 + parts.month * 100 + parts.day;
}

export function dateTimeOrd(parts: DateTimeParts): number {
  return dateOrd(parts) * 10000 + parts.hour * 100 + parts.minute;
}

export function sameDate(a: DateParts, b: DateParts): boolean {
  return a.year === b.year && a.month === b.month && a.day === b.day;
}

export function todayParts(now = new Date()): DateParts {
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

export function nextFullHour(now = new Date()): DateTimeParts {
  const d = new Date(now.getTime());
  d.setSeconds(0, 0);
  if (d.getMinutes() > 0) {
    d.setMinutes(0);
    d.setHours(d.getHours() + 1);
  }
  return {
    year: d.getFullYear(),
    month: d.getMonth() + 1,
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

function parseBound(type: string | undefined, bound: string): number | null {
  if (!bound) return null;
  if (type === "time") {
    const time = parseTimeValue(bound);
    return time ? time.hour * 100 + time.minute : null;
  }
  if (type === "month") {
    const month = parseMonthValue(bound);
    return month ? month.year * 100 + month.month : null;
  }
  if (type === "datetime-local") {
    const dt = parseDateTimeValue(bound);
    if (dt) return dateTimeOrd(dt);
    const date = parseDateValue(bound);
    return date ? dateTimeOrd({ ...date, hour: 0, minute: 0 }) : null;
  }
  const date = parseDateValue(bound);
  return date ? dateOrd(date) : null;
}

export function isDateInRange(
  parts: DateParts,
  min?: string,
  max?: string,
  type: string = "date"
): boolean {
  const value = type === "datetime-local" ? dateTimeOrd({ ...parts, hour: 0, minute: 0 }) : dateOrd(parts);
  const minN = parseBound(type === "datetime-local" ? "date" : type, min ?? "");
  const maxN = parseBound(type === "datetime-local" ? "date" : type, max ?? "");
  if (minN != null && value < minN) return false;
  if (maxN != null && value > maxN) return false;
  return true;
}

export function isTimeInRange(parts: TimeParts, min?: string, max?: string): boolean {
  const value = parts.hour * 100 + parts.minute;
  const minN = parseBound("time", min ?? "");
  const maxN = parseBound("time", max ?? "");
  if (minN != null && value < minN) return false;
  if (maxN != null && value > maxN) return false;
  return true;
}

export function isDateTimeInRange(parts: DateTimeParts, min?: string, max?: string): boolean {
  const value = dateTimeOrd(parts);
  const minN = parseBound("datetime-local", min ?? "");
  const maxN = parseBound("datetime-local", max ?? "");
  if (minN != null && value < minN) return false;
  if (maxN != null && value > maxN) return false;
  return true;
}

export function isMonthInRange(parts: MonthParts, min?: string, max?: string): boolean {
  const value = parts.year * 100 + parts.month;
  const minN = parseBound("month", min ?? "");
  const maxN = parseBound("month", max ?? "");
  if (minN != null && value < minN) return false;
  if (maxN != null && value > maxN) return false;
  return true;
}

export function clampDateToMonth(year: number, month: number, day: number): DateParts {
  return { year, month, day: Math.min(day, daysInMonth(year, month)) };
}
